import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CRMTab } from './CRMTab';
import WelcomePage from './admin/WelcomePage';
import SMSDashboard from './admin/SMSDashboard';
import QRCodeGenerator from '../components/admin/QRCodeGenerator';
import BlogManager from '../components/admin/BlogManager';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/* ═══════════════════════════════════════════
   ICONS (inline SVG for zero deps)
   ═══════════════════════════════════════════ */
const Icon = ({ d, className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);
const DashIcon = () => <Icon d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />;
const CRMIcon = () => <Icon d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />;
const PageIcon = () => <Icon d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />;
const ImageIcon = () => <Icon d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />;
const VideoIcon = () => <Icon d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />;
const UsersIcon = () => <Icon d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />;
const SettingsIcon = () => <Icon d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />;
const StarIcon = () => <Icon d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />;
const SMSIcon = () => <Icon d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />;
const WelcomeIcon = () => <Icon d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />;
const LogoutIcon = () => <Icon d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />;
const MenuIcon = () => <Icon d="M4 6h16M4 12h16M4 18h16" />;
const HelpIcon = () => <Icon d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />;

/* ═══════════════════════════════════════════
   HELP SECTION (reusable expandable guide)
   ═══════════════════════════════════════════ */
const HelpSection = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-6" data-testid="help-section">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm text-[#3a87fe] hover:text-[#2968d9] font-medium transition" data-testid="help-toggle">
        <HelpIcon />
        {open ? 'Hide Instructions' : 'How do I use this?'}
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-5 text-sm text-gray-700 leading-relaxed space-y-3" data-testid="help-content">
          {children}
        </div>
      )}
    </div>
  );
};

const QRCodeIcon = () => <Icon d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h2m6 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />;
const BlogIcon = () => <Icon d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />;

const NAV_ITEMS = [
  { id: 'welcome', label: 'Features & Guide', icon: WelcomeIcon },
  { id: 'dashboard', label: 'Dashboard', icon: DashIcon },
  { id: 'crm', label: 'CRM / Leads', icon: CRMIcon },
  { id: 'pages', label: 'Page Content', icon: PageIcon },
  { id: 'images', label: 'Media Library', icon: ImageIcon },
  { id: 'videos', label: 'Videos', icon: VideoIcon },
  { id: 'testimonials', label: 'Testimonials', icon: StarIcon },
  { id: 'blog', label: 'CT Remodel News', icon: BlogIcon },
  { id: 'sms', label: 'SMS Messaging', icon: SMSIcon },
  { id: 'qrcode', label: 'QR Code Generator', icon: QRCodeIcon },
  { id: 'users', label: 'User Management', icon: UsersIcon },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({});

  const token = localStorage.getItem('admin_token');

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
    axios.get(`${API_URL}/api/admin/auth/me`, authHeaders())
      .then(res => setUser(res.data))
      .catch(() => { localStorage.removeItem('admin_token'); navigate('/admin/login'); });
    axios.get(`${API_URL}/api/admin/stats`, authHeaders())
      .then(res => setStats(res.data))
      .catch(() => {});
  }, [token, navigate, authHeaders]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-100 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`bg-[#1a1a2e] text-white flex flex-col transition-all duration-200 ${sidebarOpen ? 'w-60' : 'w-16'} min-h-screen sticky top-0`} data-testid="admin-sidebar">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          {sidebarOpen && <span className="font-bold text-sm">CTHomeremodels CMS</span>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/60 hover:text-white p-1" data-testid="sidebar-toggle">
            <MenuIcon />
          </button>
        </div>

        <nav className="flex-1 py-4">
          {NAV_ITEMS.map(item => {
            const IconComp = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isActive ? 'bg-[#5c0701] text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
                data-testid={`nav-${item.id}`}
                title={item.label}
              >
                <IconComp />
                {sidebarOpen && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-4">
          {sidebarOpen && user && (
            <div className="text-xs text-white/40 mb-2 truncate">{user.sub || user.email}<br/><span className="text-[#3a87fe]">{user.role}</span></div>
          )}
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-2 py-2 text-sm text-white/60 hover:text-red-400 transition" data-testid="logout-btn">
            <LogoutIcon />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen overflow-x-hidden">
        <header className="bg-white shadow-sm px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <h1 className="text-xl font-bold text-[#5c0701]">
            {NAV_ITEMS.find(n => n.id === activeTab)?.label || 'Dashboard'}
          </h1>
          <div className="text-sm text-gray-400">
            Visser General Construction CMS
          </div>
        </header>

        <div className="p-6">
          {activeTab === 'welcome' && <WelcomePage userRole={user?.role} />}
          {activeTab === 'dashboard' && <DashboardOverview stats={stats} authHeaders={authHeaders} />}
          {activeTab === 'crm' && <CRMTab authHeaders={authHeaders} />}
          {activeTab === 'pages' && <PageContentEditor authHeaders={authHeaders} />}
          {activeTab === 'images' && <MediaLibrary authHeaders={authHeaders} />}
          {activeTab === 'videos' && <VideoManager authHeaders={authHeaders} />}
          {activeTab === 'testimonials' && <TestimonialManager authHeaders={authHeaders} />}
          {activeTab === 'blog' && <BlogManager authHeaders={authHeaders} />}
          {activeTab === 'sms' && <SMSDashboard authHeaders={authHeaders} />}
          {activeTab === 'qrcode' && <QRCodeGenerator />}
          {activeTab === 'users' && <UserManagement authHeaders={authHeaders} user={user} />}
          {activeTab === 'settings' && <SiteSettings authHeaders={authHeaders} />}
        </div>
      </main>
    </div>
  );
};


/* ═══════════════════════════════════════════
   DASHBOARD OVERVIEW
   ═══════════════════════════════════════════ */
const DashboardOverview = ({ stats, authHeaders }) => {
  const [crmStats, setCrmStats] = useState({});
  useEffect(() => {
    axios.get(`${API_URL}/api/crm/crm/stats`, authHeaders()).then(r => setCrmStats(r.data)).catch(() => {});
  }, [authHeaders]);

  const cards = [
    { label: 'Total Leads', value: crmStats.total_contacts || 0, color: 'bg-blue-500' },
    { label: 'New Leads', value: crmStats.new || 0, color: 'bg-green-500' },
    { label: 'Quoted', value: crmStats.quoted || 0, color: 'bg-purple-500' },
    { label: 'Won', value: crmStats.won || 0, color: 'bg-[#5c0701]' },
    { label: 'Images', value: stats.images || 0, color: 'bg-orange-500' },
    { label: 'Testimonials', value: stats.testimonials || 0, color: 'bg-yellow-500' },
  ];

  return (
    <div data-testid="dashboard-overview">
      <HelpSection>
        <p><strong>Welcome to your CTHomeRemodels Dashboard!</strong></p>
        <p>This is your home base. Here you can see a quick snapshot of your business at a glance:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Total Leads</strong> - How many people have contacted you through the website.</li>
          <li><strong>New Leads</strong> - Leads you haven't responded to yet. Check these first!</li>
          <li><strong>Quoted</strong> - Leads you've sent a price quote to.</li>
          <li><strong>Won</strong> - Jobs you've secured from website leads.</li>
          <li><strong>Images</strong> - Total project photos stored in your media library.</li>
          <li><strong>Testimonials</strong> - Customer reviews displayed on your website.</li>
        </ul>
        <p>Use the <strong>Quick Actions</strong> buttons below to preview your live website pages in a new tab.</p>
        <p>Use the <strong>left sidebar menu</strong> to navigate to each section of your dashboard.</p>
      </HelpSection>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-lg shadow-sm p-5">
            <div className={`${c.color} text-white text-2xl font-bold rounded-lg w-12 h-12 flex items-center justify-center mb-3`}>{c.value}</div>
            <div className="text-sm text-gray-500">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="font-bold text-lg mb-3">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <a href="/contact" target="_blank" rel="noopener noreferrer" className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 transition text-sm">View Contact Form</a>
          <a href="/" target="_blank" rel="noopener noreferrer" className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 transition text-sm">View Live Site</a>
          <a href="/portfolio" target="_blank" rel="noopener noreferrer" className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 transition text-sm">View Portfolio</a>
          <a href="/services" target="_blank" rel="noopener noreferrer" className="bg-gray-50 rounded-lg p-4 text-center hover:bg-gray-100 transition text-sm">View Services</a>
        </div>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════
   PAGE CONTENT EDITOR
   ═══════════════════════════════════════════ */
const PageContentEditor = ({ authHeaders }) => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newContent, setNewContent] = useState({ key: '', content: '', page: 'homepage', section: 'hero' });

  const fetchContent = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/content`, authHeaders())
      .then(res => setContents(res.data.content || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const saveContent = async (key, content, page, section) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/content`, { key, content, page, section }, authHeaders());
      setEditingKey(null);
      fetchContent();
    } catch (err) { alert('Failed to save'); }
    finally { setSaving(false); }
  };

  const addContent = async () => {
    if (!newContent.key || !newContent.content) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/content`, newContent, authHeaders());
      setShowAdd(false);
      setNewContent({ key: '', content: '', page: 'homepage', section: 'hero' });
      fetchContent();
    } catch (err) { alert('Failed to add'); }
    finally { setSaving(false); }
  };

  const pages = ['homepage', 'about', 'services', 'portfolio', 'contact'];
  const sections = ['hero', 'title', 'subtitle', 'description', 'cta', 'mission', 'process', 'faq', 'meta'];

  return (
    <div data-testid="page-content-editor">
      <HelpSection>
        <p><strong>Page Content</strong> lets you edit the text that appears on your website pages.</p>
        <p><strong>How to edit existing text:</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Find the content block you want to change and click <strong>"Edit"</strong> on the right side.</li>
          <li>Make your changes in the text box that appears, then click <strong>"Save"</strong>.</li>
          <li>Changes go live on the website immediately.</li>
        </ol>
        <p><strong>How to add new content:</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Click the blue <strong>"+ Add Content"</strong> button in the top right.</li>
          <li><strong>Content key</strong> - A short name. Use the format: <code className="bg-white px-1 rounded">hero_title</code>, <code className="bg-white px-1 rounded">hero_subtitle</code>, <code className="bg-white px-1 rounded">cta_title</code>, etc.</li>
          <li><strong>Page</strong> - Which page: homepage, about, contact, portfolio.</li>
          <li><strong>Section</strong> - Where on the page: hero, services, cta, testimonials, area.</li>
          <li>Type your text and click <strong>"Add"</strong>.</li>
        </ol>
        <p><strong>Available content keys by page:</strong></p>
        <details className="mt-2">
          <summary className="cursor-pointer text-[#3a87fe] font-medium">Homepage keys</summary>
          <ul className="list-disc ml-5 mt-1 text-xs space-y-0.5">
            <li><code>hero_badge</code> - Badge text above the main title</li>
            <li><code>hero_title</code> - Main headline</li>
            <li><code>hero_title_accent</code> - Blue accent word in headline</li>
            <li><code>hero_subtitle</code> - Description below headline</li>
            <li><code>mission</code> - Mission quote text</li>
            <li><code>services_title</code> / <code>services_subtitle</code></li>
            <li><code>testimonials_title</code> / <code>testimonials_subtitle</code></li>
            <li><code>area_title</code> / <code>area_subtitle</code></li>
            <li><code>cta_title</code> / <code>cta_subtitle</code></li>
          </ul>
        </details>
        <details className="mt-1">
          <summary className="cursor-pointer text-[#3a87fe] font-medium">About page keys</summary>
          <ul className="list-disc ml-5 mt-1 text-xs space-y-0.5">
            <li><code>hero_subtitle</code> - Tagline under "About Us"</li>
            <li><code>owner_title</code> - "Meet Cody Visser" heading</li>
            <li><code>owner_p1</code>, <code>owner_p2</code>, <code>owner_p3</code> - Bio paragraphs</li>
            <li><code>service_area</code> / <code>service_area_cities</code></li>
            <li><code>cta_title</code> / <code>cta_subtitle</code></li>
          </ul>
        </details>
        <details className="mt-1">
          <summary className="cursor-pointer text-[#3a87fe] font-medium">Contact & Portfolio keys</summary>
          <ul className="list-disc ml-5 mt-1 text-xs space-y-0.5">
            <li>Contact: <code>hero_title</code>, <code>hero_subtitle</code></li>
            <li>Portfolio: <code>hero_title</code>, <code>hero_subtitle</code>, <code>cta_title</code>, <code>cta_subtitle</code></li>
          </ul>
        </details>
        <p className="text-xs text-gray-500 mt-2">Tip: If a CMS key doesn't exist yet, the page shows the original default text. Add a key to override it.</p>
      </HelpSection>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-gray-500">Edit page text, hero titles, descriptions, and meta content. Changes apply to the live site.</p>
        <button onClick={() => setShowAdd(!showAdd)} className="bg-[#3a87fe] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition" data-testid="add-content-btn">
          {showAdd ? 'Cancel' : '+ Add Content'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-[#3a87fe]" data-testid="add-content-form">
          <h3 className="font-bold mb-4">Add New Content Block</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <input value={newContent.key} onChange={e => setNewContent({ ...newContent, key: e.target.value })} placeholder="Content key (e.g. homepage_hero_title)" className="px-3 py-2 border rounded-lg text-sm" />
            <select value={newContent.page} onChange={e => setNewContent({ ...newContent, page: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
              {pages.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={newContent.section} onChange={e => setNewContent({ ...newContent, section: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
              {sections.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={addContent} disabled={saving} className="bg-[#5c0701] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#7a0901] transition disabled:opacity-50">
              {saving ? 'Saving...' : 'Add'}
            </button>
          </div>
          <textarea value={newContent.content} onChange={e => setNewContent({ ...newContent, content: e.target.value })} placeholder="Content text..." rows="3" className="w-full px-3 py-2 border rounded-lg text-sm resize-none" />
        </div>
      )}

      {loading ? <div className="text-center py-12">Loading content...</div> : (
        <div className="space-y-3">
          {contents.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center text-gray-500">
              <p className="font-medium mb-2">No content blocks yet</p>
              <p className="text-sm">Add content blocks to make your site fully editable from this CMS.</p>
            </div>
          ) : contents.map((item, idx) => (
            <div key={item.key || idx} className="bg-white rounded-lg shadow-sm p-5" data-testid={`content-${item.key}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{item.key}</span>
                  <span className="text-xs text-gray-400 ml-2">{item.page} / {item.section}</span>
                </div>
                <button onClick={() => { setEditingKey(item.key); setEditValue(item.content); }}
                  className="text-[#3a87fe] text-xs hover:underline" data-testid={`edit-${item.key}`}>
                  {editingKey === item.key ? 'Cancel' : 'Edit'}
                </button>
              </div>
              {editingKey === item.key ? (
                <div className="mt-2">
                  <textarea value={editValue} onChange={e => setEditValue(e.target.value)} rows="4" className="w-full px-3 py-2 border rounded-lg text-sm resize-none mb-2" />
                  <button onClick={() => saveContent(item.key, editValue, item.page, item.section)} disabled={saving}
                    className="bg-[#5c0701] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              ) : (
                <p className="text-gray-700 text-sm mt-1 whitespace-pre-wrap">{item.content}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════
   MEDIA LIBRARY (Images)
   ═══════════════════════════════════════════ */
const MediaLibrary = ({ authHeaders }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState(null); // 'file' | 'url'
  const [urlInput, setUrlInput] = useState({ url: '', filename: '', alt_text: '', caption: '', category: 'portfolio' });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const fetchImages = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/images`, authHeaders())
      .then(res => setImages(res.data.images || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'portfolio');
    try {
      await axios.post(`${API_URL}/api/admin/images`, formData, { ...authHeaders(), headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' } });
      fetchImages();
    } catch (err) { alert('Upload failed'); }
    finally { setUploading(false); setUploadMode(null); }
  };

  const uploadFromUrl = async () => {
    if (!urlInput.url) return;
    setUploading(true);
    try {
      await axios.post(`${API_URL}/api/admin/images/from-url`, urlInput, authHeaders());
      setUrlInput({ url: '', filename: '', alt_text: '', caption: '', category: 'portfolio' });
      fetchImages();
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); setUploadMode(null); }
  };

  const updateImage = async (imageId) => {
    try {
      await axios.put(`${API_URL}/api/admin/images/${imageId}`, editData, authHeaders());
      setEditingId(null);
      fetchImages();
    } catch (err) { alert('Update failed'); }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/images/${imageId}`, authHeaders());
      fetchImages();
    } catch (err) { alert('Delete failed'); }
  };

  return (
    <div data-testid="media-library">
      <HelpSection>
        <p><strong>Media Library</strong> is where all your project photos are stored. These photos appear on your website pages, portfolio, and service pages.</p>
        <p><strong>How to upload a photo from your computer:</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Click the blue <strong>"Upload File"</strong> button.</li>
          <li>Click <strong>"Choose File"</strong> and select a photo from your computer or phone.</li>
          <li>The photo will upload automatically and appear in the library below.</li>
        </ol>
        <p><strong>How to upload a photo from a web link (URL):</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Click the maroon <strong>"Upload from URL"</strong> button.</li>
          <li>Paste the image web address (URL) in the <strong>"Image URL"</strong> field.</li>
          <li>Give it an <strong>SEO filename</strong> (a short descriptive name like "kitchen-remodel-wallingford"). This helps with Google rankings.</li>
          <li>Add <strong>Alt text</strong> (a brief description of what's in the photo - also helps Google find your images).</li>
          <li>Add a <strong>Caption</strong> if you want text to show below the image on the website.</li>
          <li>Choose a <strong>Category</strong> (portfolio, kitchen, bathroom, etc.) to organize your photos.</li>
          <li>Click the red <strong>"Upload"</strong> button.</li>
        </ol>
        <p><strong>How to edit or delete a photo:</strong></p>
        <ul className="list-disc ml-5 space-y-1">
          <li>Click <strong>"Edit"</strong> under any photo to change its alt text, caption, or category.</li>
          <li>Click <strong>"Delete"</strong> to permanently remove a photo. This cannot be undone.</li>
        </ul>
        <p className="text-xs text-gray-500 mt-2">Tip: All photos are safely stored in the database, so they won't be lost if the server restarts. Use descriptive filenames and alt text for better Google SEO.</p>
      </HelpSection>
      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={() => setUploadMode(uploadMode === 'file' ? null : 'file')} className="bg-[#3a87fe] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition" data-testid="upload-file-btn">Upload File</button>
        <button onClick={() => setUploadMode(uploadMode === 'url' ? null : 'url')} className="bg-[#5c0701] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#7a0901] transition" data-testid="upload-url-btn">Upload from URL</button>
        <span className="text-sm text-gray-400 self-center">{images.length} images</span>
      </div>

      {uploadMode === 'file' && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-[#3a87fe]">
          <input type="file" accept="image/*" onChange={uploadFile} disabled={uploading} className="text-sm" data-testid="file-input" />
          {uploading && <span className="ml-3 text-sm text-gray-500">Uploading...</span>}
        </div>
      )}

      {uploadMode === 'url' && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-[#5c0701]" data-testid="url-upload-form">
          <h3 className="font-bold mb-3">Upload Image from URL</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input value={urlInput.url} onChange={e => setUrlInput({ ...urlInput, url: e.target.value })} placeholder="Image URL *" className="px-3 py-2 border rounded-lg text-sm" />
            <input value={urlInput.filename} onChange={e => setUrlInput({ ...urlInput, filename: e.target.value })} placeholder="SEO filename (optional)" className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <input value={urlInput.alt_text} onChange={e => setUrlInput({ ...urlInput, alt_text: e.target.value })} placeholder="Alt text" className="px-3 py-2 border rounded-lg text-sm" />
            <input value={urlInput.caption} onChange={e => setUrlInput({ ...urlInput, caption: e.target.value })} placeholder="Caption" className="px-3 py-2 border rounded-lg text-sm" />
            <select value={urlInput.category} onChange={e => setUrlInput({ ...urlInput, category: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="portfolio">Portfolio</option>
              <option value="service">Service</option>
              <option value="hero">Hero</option>
              <option value="logo">Logo</option>
            </select>
          </div>
          <button onClick={uploadFromUrl} disabled={uploading || !urlInput.url} className="bg-[#5c0701] text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50">{uploading ? 'Uploading...' : 'Upload'}</button>
        </div>
      )}

      {loading ? <div className="text-center py-12">Loading images...</div> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, idx) => (
            <div key={img.image_id || idx} className="bg-white rounded-lg shadow-sm overflow-hidden" data-testid={`image-${idx}`}>
              <img src={`/api/media/name/${img.filename}`} alt={img.alt_text || img.filename} className="w-full h-40 object-cover" loading="lazy" />
              {editingId === (img.image_id || idx) ? (
                <div className="p-3 space-y-2">
                  <input value={editData.alt_text || ''} onChange={e => setEditData({ ...editData, alt_text: e.target.value })} placeholder="Alt text" className="w-full px-2 py-1 border rounded text-xs" />
                  <input value={editData.caption || ''} onChange={e => setEditData({ ...editData, caption: e.target.value })} placeholder="Caption" className="w-full px-2 py-1 border rounded text-xs" />
                  <select value={editData.category || ''} onChange={e => setEditData({ ...editData, category: e.target.value })} className="w-full px-2 py-1 border rounded text-xs bg-white">
                    <option value="portfolio">Portfolio</option><option value="service">Service</option><option value="hero">Hero</option><option value="logo">Logo</option>
                  </select>
                  <div className="flex gap-2">
                    <button onClick={() => updateImage(img.image_id)} className="bg-[#3a87fe] text-white px-3 py-1 rounded text-xs">Save</button>
                    <button onClick={() => setEditingId(null)} className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="p-3">
                  <p className="text-xs font-mono text-gray-500 truncate mb-1" title={img.filename}>{img.filename}</p>
                  {img.caption && <p className="text-xs text-gray-400 truncate">{img.caption}</p>}
                  {img.category && <span className="inline-block bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded mt-1">{img.category}</span>}
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => { setEditingId(img.image_id || idx); setEditData({ alt_text: img.alt_text, caption: img.caption, category: img.category }); }} className="text-[#3a87fe] text-xs hover:underline">Edit</button>
                    <button onClick={() => deleteImage(img.image_id)} className="text-red-500 text-xs hover:underline">Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════
   VIDEO MANAGER
   ═══════════════════════════════════════════ */
const VideoManager = ({ authHeaders }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', youtube_url: '', caption: '', category: 'portfolio' });
  const [saving, setSaving] = useState(false);

  const fetchVideos = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/videos`, authHeaders())
      .then(res => setVideos(res.data.videos || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const extractYouTubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const addVideo = async () => {
    if (!form.title || !form.youtube_url) return;
    const ytId = extractYouTubeId(form.youtube_url);
    if (!ytId) { alert('Invalid YouTube URL'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/videos`, {
        ...form,
        thumbnail_url: `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
      }, authHeaders());
      setForm({ title: '', youtube_url: '', caption: '', category: 'portfolio' });
      setShowAdd(false);
      fetchVideos();
    } catch (err) { alert('Failed to add video'); }
    finally { setSaving(false); }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video?')) return;
    try { await axios.delete(`${API_URL}/api/admin/videos/${videoId}`, authHeaders()); fetchVideos(); }
    catch (err) { alert('Failed'); }
  };

  return (
    <div data-testid="video-manager">
      <HelpSection>
        <p><strong>Videos</strong> lets you add YouTube videos to your website's Portfolio page.</p>
        <p><strong>How to add a YouTube video:</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Click the blue <strong>"+ Add YouTube Video"</strong> button.</li>
          <li><strong>Video title</strong> - Give it a name like "Kitchen Remodel Timelapse - Wallingford CT".</li>
          <li><strong>YouTube URL</strong> - Go to YouTube, find your video, and copy the link from the address bar. It should look like: <code className="bg-white px-1 rounded">https://www.youtube.com/watch?v=ABC123</code></li>
          <li><strong>Caption</strong> (optional) - A short description that appears below the video.</li>
          <li><strong>Category</strong> - Choose "portfolio" to show it on the Portfolio page.</li>
          <li>Click <strong>"Add Video"</strong>.</li>
        </ol>
        <p><strong>How to remove a video:</strong></p>
        <ul className="list-disc ml-5 space-y-1">
          <li>Click <strong>"Delete"</strong> under the video you want to remove.</li>
        </ul>
        <p className="text-xs text-gray-500 mt-2">Tip: Videos appear in the "Project Videos" section on your Portfolio page. Make sure you set the video to "Public" or "Unlisted" on YouTube so visitors can watch it.</p>
      </HelpSection>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-gray-500">Manage YouTube videos for your portfolio and project pages.</p>
        <button onClick={() => setShowAdd(!showAdd)} className="bg-[#3a87fe] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition" data-testid="add-video-btn">
          {showAdd ? 'Cancel' : '+ Add YouTube Video'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-[#3a87fe]" data-testid="add-video-form">
          <h3 className="font-bold mb-4">Add YouTube Video</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Video title *" className="px-3 py-2 border rounded-lg text-sm" data-testid="video-title" />
            <input value={form.youtube_url} onChange={e => setForm({ ...form, youtube_url: e.target.value })} placeholder="YouTube URL (e.g. https://youtube.com/watch?v=...) *" className="px-3 py-2 border rounded-lg text-sm" data-testid="video-url" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input value={form.caption} onChange={e => setForm({ ...form, caption: e.target.value })} placeholder="Caption / description" className="px-3 py-2 border rounded-lg text-sm" />
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="portfolio">Portfolio</option>
              <option value="testimonial">Testimonial</option>
              <option value="project">Project</option>
            </select>
          </div>
          {form.youtube_url && extractYouTubeId(form.youtube_url) && (
            <div className="mb-3">
              <img src={`https://img.youtube.com/vi/${extractYouTubeId(form.youtube_url)}/hqdefault.jpg`} alt="Preview" className="w-48 h-auto rounded" />
            </div>
          )}
          <button onClick={addVideo} disabled={saving || !form.title || !form.youtube_url}
            className="bg-[#5c0701] text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50">{saving ? 'Adding...' : 'Add Video'}</button>
        </div>
      )}

      {loading ? <div className="text-center py-12">Loading videos...</div> : videos.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">
          <p className="font-medium mb-2">No videos yet</p>
          <p className="text-sm">Add YouTube video URLs to showcase your work.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((v, idx) => {
            const ytId = extractYouTubeId(v.youtube_url);
            return (
              <div key={v.video_id || idx} className="bg-white rounded-lg shadow-sm overflow-hidden" data-testid={`video-${idx}`}>
                {ytId ? (
                  <div className="relative pb-[56.25%]">
                    <iframe src={`https://www.youtube.com/embed/${ytId}`} title={v.title} className="absolute inset-0 w-full h-full" allowFullScreen loading="lazy" />
                  </div>
                ) : (
                  <div className="bg-gray-200 h-40 flex items-center justify-center text-gray-400">No preview</div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-sm mb-1">{v.title}</h3>
                  {v.caption && <p className="text-xs text-gray-500 mb-2">{v.caption}</p>}
                  <div className="flex justify-between items-center">
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">{v.category}</span>
                    <button onClick={() => deleteVideo(v.video_id)} className="text-red-500 text-xs hover:underline">Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════
   TESTIMONIAL MANAGER
   ═══════════════════════════════════════════ */
const SERVICES_LIST = [
  'Kitchen Remodeling', 'Bathroom Remodeling', 'Deck Building / Trex Decking',
  'Basement Finishing', 'Vinyl Siding', 'Window Replacement',
  'Custom Furniture & Aquariums', 'Home Maintenance'
];

const TestimonialManager = ({ authHeaders }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', city: '', service: '', text: '', rating: 5, is_visible: true });

  const fetchTestimonials = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/testimonials`, authHeaders())
      .then(res => setTestimonials(res.data.testimonials || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { fetchTestimonials(); }, [fetchTestimonials]);

  const resetForm = () => {
    setForm({ name: '', city: '', service: '', text: '', rating: 5, is_visible: true });
    setShowAdd(false);
    setEditingId(null);
  };

  const saveTestimonial = async () => {
    if (!form.name || !form.text) return;
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/testimonials/${editingId}`, form, authHeaders());
      } else {
        await axios.post(`${API_URL}/api/admin/testimonials`, form, authHeaders());
      }
      resetForm();
      fetchTestimonials();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const startEdit = (t) => {
    setForm({ name: t.name, city: t.city, service: t.service, text: t.text, rating: t.rating, is_visible: t.is_visible });
    setEditingId(t.testimonial_id);
    setShowAdd(true);
  };

  const toggleVisibility = async (t) => {
    try {
      await axios.put(`${API_URL}/api/admin/testimonials/${t.testimonial_id}`, { is_visible: !t.is_visible }, authHeaders());
      fetchTestimonials();
    } catch { alert('Failed'); }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try { await axios.delete(`${API_URL}/api/admin/testimonials/${id}`, authHeaders()); fetchTestimonials(); }
    catch { alert('Failed'); }
  };

  const StarRating = ({ rating, onChange }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button key={star} type="button" onClick={() => onChange && onChange(star)}
          className={`text-lg ${star <= rating ? 'text-yellow-400' : 'text-gray-300'} ${onChange ? 'cursor-pointer hover:text-yellow-500' : ''}`}>
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="testimonial-manager">
      <HelpSection>
        <p><strong>Testimonials</strong> lets you manage the customer reviews that appear on your homepage and service pages.</p>
        <p><strong>How to add a customer review:</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Click <strong>"+ Add Testimonial"</strong>.</li>
          <li><strong>Customer name</strong> - Enter their name (e.g., "Michael & Sarah Johnson").</li>
          <li><strong>City</strong> - Enter their city (e.g., "Wallingford, CT"). This helps with local SEO.</li>
          <li><strong>Service</strong> - Select which service they had done (Kitchen Remodeling, Deck Building, etc.).</li>
          <li><strong>Testimonial text</strong> - Type or paste what the customer said about your work.</li>
          <li><strong>Rating</strong> - Click the stars to set their rating (1 to 5 stars).</li>
          <li><strong>Visible on site</strong> - Check this box to show it on your website. Uncheck to hide it.</li>
          <li>Click <strong>"Add Testimonial"</strong> to save.</li>
        </ol>
        <p><strong>Managing existing reviews:</strong></p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Edit</strong> - Change the text, rating, or any details.</li>
          <li><strong>Hide/Show</strong> - Temporarily hide a review from your website without deleting it. Click "Show" to bring it back.</li>
          <li><strong>Delete</strong> - Permanently remove a review. This cannot be undone.</li>
        </ul>
        <p>Reviews with a <span className="inline-block w-3 h-3 bg-green-400 rounded-sm align-middle"></span> green bar on the left are visible on your website. <span className="inline-block w-3 h-3 bg-gray-300 rounded-sm align-middle"></span> Gray means hidden.</p>
        <p className="text-xs text-gray-500 mt-2">Coming soon: Google Business Profile integration will automatically pull your Google reviews here.</p>
      </HelpSection>
      <div className="flex justify-between items-center mb-6">
        <div>
          <p className="text-sm text-gray-500">Manage customer testimonials displayed on the homepage.</p>
          <p className="text-xs text-gray-400 mt-1">Source: Manual entries now, Google Business Profile integration coming soon.</p>
        </div>
        <button onClick={() => { if (showAdd) resetForm(); else setShowAdd(true); }}
          className="bg-[#3a87fe] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition" data-testid="add-testimonial-btn">
          {showAdd ? 'Cancel' : '+ Add Testimonial'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-[#3a87fe]" data-testid="testimonial-form">
          <h3 className="font-bold mb-4">{editingId ? 'Edit Testimonial' : 'Add New Testimonial'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Customer name *"
              className="px-3 py-2 border rounded-lg text-sm" data-testid="testimonial-name" />
            <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="City (e.g. Meriden, CT)"
              className="px-3 py-2 border rounded-lg text-sm" data-testid="testimonial-city" />
            <select value={form.service} onChange={e => setForm({ ...form, service: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="testimonial-service">
              <option value="">Select service</option>
              {SERVICES_LIST.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <textarea value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} placeholder="Testimonial text *" rows="3"
            className="w-full px-3 py-2 border rounded-lg text-sm resize-none mb-3" data-testid="testimonial-text" />
          <div className="flex items-center gap-6 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Rating:</span>
              <StarRating rating={form.rating} onChange={r => setForm({ ...form, rating: r })} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_visible} onChange={e => setForm({ ...form, is_visible: e.target.checked })}
                className="w-4 h-4 text-[#3a87fe] rounded" data-testid="testimonial-visible" />
              <span className="text-sm text-gray-600">Visible on site</span>
            </label>
          </div>
          <button onClick={saveTestimonial} disabled={saving || !form.name || !form.text}
            className="bg-[#5c0701] text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50" data-testid="testimonial-save">
            {saving ? 'Saving...' : (editingId ? 'Update Testimonial' : 'Add Testimonial')}
          </button>
        </div>
      )}

      {loading ? <div className="text-center py-12">Loading testimonials...</div> : testimonials.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">
          <p className="font-medium mb-2">No testimonials yet</p>
          <p className="text-sm">Add customer reviews to display on your homepage.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {testimonials.map((t, idx) => (
            <div key={t.testimonial_id || idx} className={`bg-white rounded-lg shadow-sm p-5 border-l-4 ${t.is_visible ? 'border-green-400' : 'border-gray-300'}`} data-testid={`testimonial-${idx}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-semibold text-sm">{t.name}</span>
                    {t.city && <span className="text-xs text-gray-400">{t.city}</span>}
                    {t.service && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{t.service}</span>}
                    <span className={`text-xs px-2 py-0.5 rounded ${t.source === 'gbp' ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-500'}`}>
                      {t.source === 'gbp' ? 'Google' : 'Manual'}
                    </span>
                  </div>
                  <StarRating rating={t.rating} />
                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">"{t.text}"</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => startEdit(t)} className="text-[#3a87fe] text-xs hover:underline">Edit</button>
                  <button onClick={() => toggleVisibility(t)} className={`text-xs hover:underline ${t.is_visible ? 'text-orange-500' : 'text-green-600'}`}>
                    {t.is_visible ? 'Hide' : 'Show'}
                  </button>
                  <button onClick={() => deleteTestimonial(t.testimonial_id)} className="text-red-500 text-xs hover:underline">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════
   USER MANAGEMENT
   ═══════════════════════════════════════════ */
const UserManagement = ({ authHeaders, user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'admin' });
  const [pwModal, setPwModal] = useState({ show: false, email: '', newPassword: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const isSuperadmin = user?.role === 'superadmin';

  const fetchUsers = useCallback(() => {
    if (!isSuperadmin) { setLoading(false); return; }
    setLoading(true);
    axios.get(`${API_URL}/api/admin/users`, authHeaders())
      .then(res => setUsers(res.data.users || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authHeaders, isSuperadmin]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const addUser = async () => {
    if (!newUser.email || !newUser.password) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, newUser, authHeaders());
      setShowAdd(false);
      setNewUser({ email: '', password: '', name: '', role: 'admin' });
      setMessage('User created successfully');
      fetchUsers();
    } catch (err) { setMessage(err.response?.data?.detail || 'Failed to create user'); }
    finally { setSaving(false); setTimeout(() => setMessage(''), 3000); }
  };

  const toggleSuspend = async (email) => {
    try {
      const res = await axios.patch(`${API_URL}/api/admin/users/${encodeURIComponent(email)}/suspend`, {}, authHeaders());
      setMessage(res.data.message);
      fetchUsers();
    } catch (err) { setMessage(err.response?.data?.detail || 'Failed'); }
    finally { setTimeout(() => setMessage(''), 3000); }
  };

  const changePassword = async () => {
    if (!pwModal.newPassword || pwModal.newPassword.length < 8) { 
      setMessage('Password must be at least 8 characters'); 
      return; 
    }
    if (pwModal.newPassword !== pwModal.confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${encodeURIComponent(pwModal.email)}/reset-pw`, { new_password: pwModal.newPassword }, authHeaders());
      setPwModal({ show: false, email: '', newPassword: '', confirmPassword: '' });
      setShowPw(false);
      setShowConfirmPw(false);
      setMessage('Password updated');
    } catch (err) { setMessage(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); setTimeout(() => setMessage(''), 3000); }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${encodeURIComponent(email)}`, authHeaders());
      setMessage('User deleted');
      fetchUsers();
    } catch (err) { setMessage(err.response?.data?.detail || 'Failed'); }
    finally { setTimeout(() => setMessage(''), 3000); }
  };

  if (!isSuperadmin) {
    return (
      <div className="bg-white rounded-lg p-8 text-center">
        <h2 className="text-xl font-bold text-[#5c0701] mb-2">Access Restricted</h2>
        <p className="text-gray-500">Only superadmin users can manage other users.</p>
      </div>
    );
  }

  return (
    <div data-testid="user-management">
      {message && <div className="bg-blue-50 text-blue-700 border border-blue-200 px-4 py-3 rounded-lg mb-4 text-sm">{message}</div>}

      <HelpSection>
        <p><strong>User Management</strong> lets you control who can log in to this admin dashboard.</p>
        <p><strong>User Roles:</strong></p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Superadmin</strong> (purple badge) - Full access to everything, including managing other users. This is your account.</li>
          <li><strong>Admin</strong> (blue badge) - Can manage content, media, videos, testimonials, and leads, but cannot manage other users.</li>
        </ul>
        <p><strong>How to add a new admin user:</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Click <strong>"+ Add User"</strong>.</li>
          <li>Enter their <strong>Name</strong>, <strong>Email</strong>, and a <strong>Password</strong>.</li>
          <li>Choose their <strong>Role</strong> (admin is recommended for team members).</li>
          <li>Click <strong>"Create User"</strong>.</li>
          <li>Share the email and password with them so they can log in.</li>
        </ol>
        <p><strong>Managing existing users:</strong></p>
        <ul className="list-disc ml-5 space-y-1">
          <li><strong>Password</strong> - Click to change a user's password. Useful if someone forgets theirs.</li>
          <li><strong>Suspend</strong> - Temporarily block a user from logging in. They won't be deleted, just locked out. Click "Reactivate" to restore access.</li>
          <li><strong>Delete</strong> - Permanently remove a user. This cannot be undone.</li>
        </ul>
        <p className="text-xs text-gray-500 mt-2">Tip: Only Superadmins can see this page. Regular admins don't have access to user management.</p>
      </HelpSection>

      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-gray-500">Add, suspend, or change passwords for admin users.</p>
        <button onClick={() => setShowAdd(!showAdd)} className="bg-[#3a87fe] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition" data-testid="add-user-btn">
          {showAdd ? 'Cancel' : '+ Add User'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-[#3a87fe]" data-testid="add-user-form">
          <h3 className="font-bold mb-4">Add New Admin User</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} placeholder="Name" className="px-3 py-2 border rounded-lg text-sm" data-testid="new-user-name" />
            <input value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="Email *" type="email" className="px-3 py-2 border rounded-lg text-sm" data-testid="new-user-email" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div className="relative">
              <input value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="Password (min 8 chars) *" type={showPw ? 'text' : 'password'} className="w-full px-3 py-2 pr-10 border rounded-lg text-sm" data-testid="new-user-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{showPw ? 'Hide' : 'Show'}</button>
            </div>
            <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })} className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="new-user-role">
              <option value="admin">Admin</option>
              <option value="superadmin">Superadmin</option>
            </select>
          </div>
          <button onClick={addUser} disabled={saving || !newUser.email || !newUser.password}
            className="bg-[#5c0701] text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50" data-testid="create-user-submit">
            {saving ? 'Creating...' : 'Create User'}
          </button>
        </div>
      )}

      {/* Password Change Modal */}
      {pwModal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setPwModal({ show: false, email: '', newPassword: '', confirmPassword: '' }); setShowPw(false); setShowConfirmPw(false); }}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="pw-change-modal">
            <h3 className="font-bold text-lg mb-4">Change Password for {pwModal.email}</h3>
            
            {/* New Password Field */}
            <div className="relative mb-3">
              <input 
                value={pwModal.newPassword} 
                onChange={e => setPwModal({ ...pwModal, newPassword: e.target.value })}
                type={showPw ? "text" : "password"} 
                placeholder="New password (min 8 characters)" 
                className="w-full px-4 py-3 border rounded-lg pr-12" 
                data-testid="pw-new-input" 
              />
              <button 
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="pw-toggle-new"
              >
                {showPw ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            
            {/* Confirm Password Field */}
            <div className="relative mb-4">
              <input 
                value={pwModal.confirmPassword} 
                onChange={e => setPwModal({ ...pwModal, confirmPassword: e.target.value })}
                type={showConfirmPw ? "text" : "password"} 
                placeholder="Confirm new password" 
                className={`w-full px-4 py-3 border rounded-lg pr-12 ${
                  pwModal.confirmPassword && pwModal.newPassword !== pwModal.confirmPassword 
                    ? 'border-red-300 bg-red-50' 
                    : pwModal.confirmPassword && pwModal.newPassword === pwModal.confirmPassword
                    ? 'border-green-300 bg-green-50'
                    : ''
                }`}
                data-testid="pw-confirm-input" 
              />
              <button 
                type="button"
                onClick={() => setShowConfirmPw(!showConfirmPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="pw-toggle-confirm"
              >
                {showConfirmPw ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            
            {/* Password match indicator */}
            {pwModal.confirmPassword && (
              <p className={`text-xs mb-3 ${pwModal.newPassword === pwModal.confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                {pwModal.newPassword === pwModal.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
              </p>
            )}
            
            <div className="flex gap-3">
              <button 
                onClick={changePassword} 
                disabled={saving || !pwModal.newPassword || pwModal.newPassword !== pwModal.confirmPassword} 
                className="bg-[#5c0701] text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Updating...' : 'Update Password'}
              </button>
              <button onClick={() => { setPwModal({ show: false, email: '', newPassword: '', confirmPassword: '' }); setShowPw(false); setShowConfirmPw(false); }} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-12">Loading users...</div> : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-semibold">Name</th>
                <th className="text-left p-4 font-semibold">Email</th>
                <th className="text-left p-4 font-semibold">Role</th>
                <th className="text-left p-4 font-semibold">Status</th>
                <th className="text-left p-4 font-semibold">Created</th>
                <th className="text-left p-4 font-semibold w-48">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <tr key={u.email || idx} className="border-b hover:bg-gray-50" data-testid={`user-row-${idx}`}>
                  <td className="p-4 font-medium">{u.name || '-'}</td>
                  <td className="p-4">{u.email}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.role === 'superadmin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{u.role}</span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {u.is_active !== false ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td className="p-4 text-gray-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button onClick={() => setPwModal({ show: true, email: u.email, newPassword: '', confirmPassword: '' })}
                        className="text-[#3a87fe] text-xs hover:underline" data-testid={`pw-btn-${idx}`}>Password</button>
                      <button onClick={() => toggleSuspend(u.email)}
                        className={`text-xs hover:underline ${u.is_active !== false ? 'text-orange-500' : 'text-green-600'}`} data-testid={`suspend-btn-${idx}`}>
                        {u.is_active !== false ? 'Suspend' : 'Reactivate'}
                      </button>
                      <button onClick={() => deleteUser(u.email)} className="text-red-500 text-xs hover:underline" data-testid={`delete-btn-${idx}`}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════
   SITE SETTINGS
   ═══════════════════════════════════════════ */
const SiteSettings = ({ authHeaders }) => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSetting, setNewSetting] = useState({ key: '', value: '', category: 'contact' });
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [indexNowStatus, setIndexNowStatus] = useState('');

  const fetchSettings = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/admin/settings`, authHeaders())
      .then(res => setSettings(res.data.settings || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveSetting = async (key, value, category) => {
    try {
      await axios.post(`${API_URL}/api/admin/settings`, { key, value, category }, authHeaders());
      setEditingKey(null);
      fetchSettings();
    } catch { alert('Failed to save'); }
  };

  const addSetting = async () => {
    if (!newSetting.key || !newSetting.value) return;
    try {
      await axios.post(`${API_URL}/api/admin/settings`, newSetting, authHeaders());
      setShowAdd(false);
      setNewSetting({ key: '', value: '', category: 'contact' });
      fetchSettings();
    } catch { alert('Failed'); }
  };

  return (
    <div data-testid="site-settings">
      <HelpSection>
        <p><strong>Settings</strong> stores important configuration values for your website.</p>
        <p><strong>How to add a new setting:</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Click <strong>"+ Add Setting"</strong>.</li>
          <li><strong>Setting key</strong> - A short label like "company_phone" or "office_hours".</li>
          <li><strong>Value</strong> - The actual data, like "(203) 604-3202" or "Mon-Fri 5:00 AM - 5:30 PM".</li>
          <li><strong>Category</strong> - Choose a category to keep things organized (Contact, Email, SMS, Social, SEO).</li>
          <li>Click <strong>"Add"</strong>.</li>
        </ol>
        <p><strong>How to edit a setting:</strong></p>
        <ul className="list-disc ml-5">
          <li>Click <strong>"Edit"</strong> next to any setting, change the value, and click <strong>"Save"</strong>.</li>
        </ul>
        <p><strong>What is "Submit to IndexNow"?</strong></p>
        <ul className="list-disc ml-5">
          <li>This tells Google and Bing that your website has been updated, so they re-crawl your pages faster.</li>
          <li>Click the green <strong>"Submit to IndexNow"</strong> button after making significant content changes to help your pages show up in search results sooner.</li>
          <li>You don't need to click this every time - just after major updates like adding new service pages or lots of new photos.</li>
        </ul>
      </HelpSection>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-gray-500">Manage site-wide settings like contact info, email config, and integrations.</p>
        <div className="flex gap-2">
          <button onClick={async () => {
            setIndexNowStatus('Submitting...');
            try {
              const res = await axios.post(`${API_URL}/api/indexnow`, { urls: [
                'https://cthomeremodels.com', 'https://cthomeremodels.com/services', 'https://cthomeremodels.com/portfolio',
                'https://cthomeremodels.com/about', 'https://cthomeremodels.com/contact'
              ] }, authHeaders());
              setIndexNowStatus(res.data.status === 'success' ? 'Submitted to IndexNow!' : res.data.message);
            } catch { setIndexNowStatus('Failed'); }
            setTimeout(() => setIndexNowStatus(''), 3000);
          }} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition" data-testid="indexnow-btn">
            {indexNowStatus || 'Submit to IndexNow'}
          </button>
          <button onClick={() => setShowAdd(!showAdd)} className="bg-[#3a87fe] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2968d9] transition">
            {showAdd ? 'Cancel' : '+ Add Setting'}
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-[#3a87fe]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <input value={newSetting.key} onChange={e => setNewSetting({ ...newSetting, key: e.target.value })} placeholder="Setting key" className="px-3 py-2 border rounded-lg text-sm" />
            <input value={newSetting.value} onChange={e => setNewSetting({ ...newSetting, value: e.target.value })} placeholder="Value" className="px-3 py-2 border rounded-lg text-sm" />
            <div className="flex gap-2">
              <select value={newSetting.category} onChange={e => setNewSetting({ ...newSetting, category: e.target.value })} className="flex-1 px-3 py-2 border rounded-lg text-sm bg-white">
                <option value="contact">Contact</option><option value="email">Email</option><option value="sms">SMS</option><option value="social">Social</option><option value="seo">SEO</option>
              </select>
              <button onClick={addSetting} className="bg-[#5c0701] text-white px-4 py-2 rounded-lg text-sm">Add</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-12">Loading...</div> : (
        <div className="space-y-3">
          {settings.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center text-gray-500">No settings yet</div>
          ) : settings.map((s, idx) => (
            <div key={s.key || idx} className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between">
              <div className="flex-1">
                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{s.key}</span>
                <span className="text-xs text-gray-400 ml-2">{s.category}</span>
                {editingKey === s.key ? (
                  <div className="flex gap-2 mt-2">
                    <input value={editValue} onChange={e => setEditValue(e.target.value)} className="flex-1 px-3 py-1.5 border rounded text-sm" />
                    <button onClick={() => saveSetting(s.key, editValue, s.category)} className="bg-[#3a87fe] text-white px-3 py-1.5 rounded text-xs">Save</button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-700 mt-1">{s.value}</p>
                )}
              </div>
              <button onClick={() => { setEditingKey(s.key); setEditValue(s.value); }} className="text-[#3a87fe] text-xs hover:underline ml-4">
                {editingKey === s.key ? 'Cancel' : 'Edit'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default AdminDashboard;
